import React, { useContext } from 'react';
import {
  BodyText,
  SpruceHelpInlineLink,
  Button,
  CheckboxInput,
  Grid,
  Header3Text,
  Icon,
  IconSize,
  IconType,
  ISpruceFieldProps,
  ModalWindow,
  SpruceArrayField,
  SwitchDialogWindows,
} from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { Form, Formik } from 'formik';
import { ThemeContext } from 'styled-components';
import { finalizePoliciesSchema } from './finalizePoliciesSchema';
import { RotatingIcon } from '../../../minipieces/documents/upload/RotatingIcon';
import { IStandardDocument } from '../../../types/order/documents/shared/interfaces';

interface IFinalizePoliciesProps {
  fetchErrors: Error[];
  fetchStatus: IFetchStatus;
  feesCount: number;
  policyDocuments: IStandardDocument[];
  sendErrors: Error[];
  sendStatus: IFetchStatus;
  submittedDocumentIds: string[];
  showModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  onSubmit: (documentIds: string[]) => void;
  getCategoryLabel: (categoryName: string) => string;
}

export const FinalizePolicies: React.FC<IFinalizePoliciesProps> = ({
  fetchErrors,
  fetchStatus,
  feesCount,
  policyDocuments,
  sendErrors,
  sendStatus,
  submittedDocumentIds,
  showModal,
  closeModal,
  openModal,
  onSubmit,
  getCategoryLabel,
}) => {
  const theme = useContext(ThemeContext);
  return (
    <>
      {fetchStatus.fetching ? (
        <RotatingIcon type={IconType.Loading} />
      ) : fetchErrors.length ? (
        fetchErrors.map((error, index) => <BodyText key={index}>{error.message}</BodyText>)
      ) : (
        <Button
          intent={'primary'}
          disabled={
            !policyDocuments.length ||
            policyDocuments.length !== feesCount ||
            policyDocuments.every((document) => !!document.client_published_at)
          }
          onClick={openModal}
        >
          Publish
        </Button>
      )}
      {showModal && (
        <SwitchDialogWindows
          errorMessage={
            <>
              {sendErrors.map((value, index) => (
                <BodyText key={index}>{value.message}</BodyText>
              ))}
            </>
          }
          errorTitle={
            <Header3Text>
              There was an error publishing policies. Please <SpruceHelpInlineLink /> to submit a request for help.
            </Header3Text>
          }
          isCompleted={sendStatus.fetched}
          isLoading={sendStatus.fetching}
          isError={sendStatus.fetched && !sendStatus.success}
          loadingTitle="Publishing..."
          onCloseClick={closeModal}
          successTitle="Success!"
          successMessage={
            <BodyText>
              {policyDocuments
                .filter(({ id }) => submittedDocumentIds.includes(id))
                .map(({ category }) => getCategoryLabel(category) || '')
                .join(' and ')}
              {` ${submittedDocumentIds.length > 1 ? 'have' : 'has'} `}
              been successfully published
            </BodyText>
          }
        >
          <ModalWindow uitype="fit" onClose={closeModal}>
            <Formik
              initialValues={{
                policies: [],
              }}
              validationSchema={finalizePoliciesSchema}
              onSubmit={(values, formikHelpers) => {
                onSubmit(values.policies);
              }}
            >
              <Form style={{ textAlign: 'left' }}>
                <Grid>
                  <div style={{ textAlign: 'center' }}>
                    <Icon type={IconType.Rocket} size={IconSize.Large} color={theme.colors.blue} />
                  </div>
                  <Header3Text>Select document to publish:</Header3Text>
                  <SpruceArrayField name="policies">
                    {({ field }: ISpruceFieldProps) => (
                      <>
                        {policyDocuments.map((pd) =>
                          !pd.client_published_at ? (
                            <CheckboxInput
                              {...field}
                              key={pd.id}
                              text={getCategoryLabel(pd.category)}
                              onBlur={() => undefined}
                              onChange={(value) => {
                                field.onChange(
                                  value ? [...field.value, pd.id] : field.value.filter((v: string) => v !== pd.id)
                                );
                              }}
                              value={field.value.includes(pd.id)}
                            />
                          ) : (
                            <CheckboxInput
                              key={pd.id}
                              text={getCategoryLabel(pd.category)}
                              disabled={true}
                              value={true}
                            />
                          )
                        )}
                      </>
                    )}
                  </SpruceArrayField>
                  <Grid columnCount={2}>
                    <div>
                      <Button intent={'primary'} type="submit">
                        Publish
                      </Button>
                    </div>
                    <div>
                      <Button intent={'secondary'} type="button" onClick={closeModal}>
                        Cancel
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
          </ModalWindow>
        </SwitchDialogWindows>
      )}
    </>
  );
};
